// RankingSection.js
import React from 'react';
import { TrendingUp, TrendingDown, Crown, Trophy, ChevronRight } from 'lucide-react';

const RankingSection = ({ rankingData, onStockClick, onOpenRanking }) => {

  // 상위 3개만 표시
  const topThree = rankingData?.list?.slice(0, 3) || [];

  // insertDateTime의 최대값 찾기
  const getLatestDateTime = () => {
    if (!rankingData?.list || rankingData.list.length === 0) return null;
    
    const dates = rankingData.list
      .map(item => item.insertDateTime)
      .filter(date => date != null);
    
    if (dates.length === 0) return null;
    
    return new Date(Math.max(...dates.map(date => new Date(date).getTime())));
  };

  const latestDateTime = getLatestDateTime();

  // 주식 로고 경로 생성
  const getStockLogoPath = (ticker) => {
    return `/CompanyLogo/${ticker}.svg`;
  };

  // 순위 아이콘
  const getRankIcon = (rank) => {
    if (rank === 1) {
      return <Crown className="w-5 h-5 text-yellow-400" fill="currentColor" />;
    } else if (rank === 2) {
      return <Crown className="w-5 h-5 text-blue-200" fill="currentColor" />;
    } else if (rank === 3) {
      return <Crown className="w-5 h-5 text-yellow-200" fill="currentColor" />;
    }
    return null;
  };

  // 점수에 따른 배지 스타일 (TradingJournal 색상 통일)
  const getScoreBadge = (score) => {
    if (score >= 70) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-600',
        border: 'border-green-200',
      };
    } else if (score >= 40) {
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-600',
        border: 'border-blue-200',
      };
    } else if (score >= 0) {
      return {
        bg: 'bg-gray-50',
        text: 'text-gray-600',
        border: 'border-gray-200',
      };
    } else if (score >= -40) {
      return {
        bg: 'bg-pink-100',
        text: 'text-red-600',
        border: 'border-red-200',
      };
    } else {
      return {
        bg: 'bg-pink-100',
        text: 'text-red-600',
        border: 'border-red-200',
      };
    }
  };

  // 상태 배지 (TradingJournal 색상 통일)
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'SELL': return { text: '매도', color: 'bg-pink-100 text-red-600' };
      case 'BUY': return { text: '매수', color: 'bg-green-100 text-green-600' };
      case 'HOLDING': return { text: '보유', color: 'bg-blue-50 text-blue-600' };
      case 'NO_POSITION': return { text: '미보유', color: 'bg-gray-100 text-gray-500' };
      default: return { text: '미보유', color: 'bg-gray-100 text-gray-500' };
    }
  };

  // 수익률 색상
  const getProfitColor = (profit) => {
    if (profit > 0) return 'text-blue-600';
    if (profit < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // 수익률 포맷
  const formatProfit = (profit) => {
    const percent = profit * 100;
    return percent > 0 ? `+${percent.toFixed(2)}%` : `${percent.toFixed(2)}%`;
  };


  return (
    <div className="feature-section">
      {/* Header */}

  <div className="flex items-center gap-1 mb-2">
    <div className="feature-icon">
      <TrendingUp size={20} />
    </div>
    <h2 className="text-xm font-bold text-black">오늘의 AI 주식 랭킹</h2>
  </div>
      {/* <p className="text-sm text-gray-600 mb-4">
      상위 종목 랭킹
      </p> */}

      {/* 상위 3개 카드 */}
      <div className="space-y-3 mb-4">
        {topThree.map((stock, index) => {
          const scoreBadge = getScoreBadge(stock.score);
          const statusInfo = getStatusDisplay(stock.status);

          return (
            <div
              key={stock.ticker}
              className={`bg-white/80 backdrop-blur-sm rounded-2xl hover:shadow-xl transition-all duration-300 p-4 md:p-5 border cursor-pointer ${
                index < 3
                  ? 'border-blue-200 shadow-lg shadow-blue-100/50'
                  : 'border-slate-200 shadow-md hover:border-blue-200'
              }`}
              onClick={() => onStockClick(stock.ticker)}
            >
              {/* 모바일: 2줄 레이아웃 */}
              <div className="md:hidden">
                {/* 첫 번째 줄: 순위 + 로고 + 티커 + 점수 */}
                <div className="flex items-center gap-3 mb-3">
                  {/* 순위 */}
                  <div className="flex-shrink-0">
                    {getRankIcon(index + 1)}
                  </div>

                  {/* 로고 */}
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 shadow-md overflow-hidden">
                      <img
                        src={getStockLogoPath(stock.ticker)}
                        alt={stock.ticker}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <span className="text-lg text-gray-400" style={{ display: 'none' }}>
                        {stock.ticker.charAt(0)}
                      </span>
                    </div>
                  </div>

                  {/* 티커 + 상태 */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900 truncate">{stock.ticker}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-md font-medium inline-block ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>

                  {/* AI 점수 */}
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xl font-bold text-gray-900">{stock.score}점</div>
                  </div>
                </div>

                {/* 두 번째 줄: 통계 + 배지 */}
                <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
                  <div className="flex flex-col gap-1 text-xs text-gray-600 flex-1">
                    <div>거래 추적일 <span className="font-semibold text-gray-900">{stock.monitoringDayCount}일</span></div>
                    <div className={getProfitColor(stock.totalProfit)}>
                      시뮬레이션 수익률 <span className="font-semibold">{formatProfit(stock.totalProfit)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 데스크톱: 1줄 레이아웃 */}
              <div className="hidden md:flex items-center gap-4">
                {/* 순위 */}
                <div className="flex items-center justify-center min-w-[50px]">
                  <div className="flex flex-col items-center">
                    {getRankIcon(index + 1)}
                    <span className="text-xs text-gray-500 mt-1">#{index + 1}</span>
                  </div>
                </div>

                {/* 로고 */}
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 shadow-lg overflow-hidden">
                    <img
                      src={getStockLogoPath(stock.ticker)}
                      alt={stock.ticker}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <span className="text-xl text-gray-400" style={{ display: 'none' }}>
                      {stock.ticker.charAt(0)}
                    </span>
                  </div>
                </div>

                {/* 정보 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-gray-900">{stock.ticker}</h3>
                    <span className={`text-xs px-2 py-1 rounded-md font-medium ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span>거래 추적일 {stock.monitoringDayCount}일</span>
                    <span className="text-gray-300">|</span>
                    <span className={getProfitColor(stock.totalProfit)}>
                      시뮬레이션 수익률 {formatProfit(stock.totalProfit)}
                    </span>
                  </div>
                </div>

                {/* AI 점수 */}
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 mb-1">{stock.score}점</div>
                  <div className={`flex items-center justify-end gap-1 text-sm ${
                    stock.score >= 50 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {stock.score >= 50 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span>AI 분석</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 업데이트 시간 */}
      <div className="text-xs text-gray-400 text-right mt-2">
        {latestDateTime ? new Date(latestDateTime).toLocaleString('ko-KR') : 'N/A'}
      </div>

      {/* 전체 보기 버튼 */}
      <button
        onClick={onOpenRanking}
        className="view-full-button mt-2"
      >
        <span>전체 랭킹 보기</span>
        <ChevronRight size={16} />
      </button>

    </div>
  );
};

export default RankingSection;
